import React from 'react';
import { EmployeeUser, JobTitle } from '../../../../../utils/types/admin_types';
import {
  AutoAssessment,
  AutoEvaluation,
  JobDescription,
  JobPostingTabData,
  JobRequisition,
  jobOpeningDetailsTabs,
} from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { formatJobTitleOption, safeJobUserToUserDisplay } from '../functions/jobOpeningTransformers';
import { IJobOpeningDetails } from '../Interfaces/jobOpeningDetails.interface';
import { AutoAssessmentTab } from '../AutoAssessmentTab';
import { AutoEvaluationTab } from '../AutoEvaluationTab';
import CVIngestionTab from '../CVIngestion';
import JobDescriptionTab from '../JobDescription';
import JobPosting from '../JobPosting';
import CCATab from '../JobPosting/CCATab';
import { JobRequisitionPage } from '../JobRequisition/JobRequsitionPage';
import { Permissions } from '../Permissions';

interface IJobOpeningTabRendererProps extends IJobOpeningDetails {
  currentSubTab: jobOpeningDetailsTabs;
  jobOpeningId?: string;
  editable: boolean;
  completedElement: () => void;
  jobTitleList: JobTitle[];
  defaultEmployeeUserList: EmployeeUser[];
  hiringManagerOptions?: SelectOptions[];
  internalRecruiterOptions?: SelectOptions[];
  setHiringManagerOptions: React.Dispatch<React.SetStateAction<SelectOptions[] | undefined>>;
  setInternalRecruiterOptions: React.Dispatch<React.SetStateAction<SelectOptions[] | undefined>>;
}

export const JobOpeningTabRenderer = (props: IJobOpeningTabRendererProps) => {
  const {
    currentSubTab,
    jobOpeningId,
    editable,
    completedElement,
    jobTitleList,
    defaultEmployeeUserList,
    hiringManagerOptions,
    internalRecruiterOptions,
    setHiringManagerOptions,
    setInternalRecruiterOptions,
    jobRequisition,
    setJobRequisition,
    jobDescription,
    setJobDescription,
    autoSuitability,
    setAutoSuitability,
    autoOutstanding,
    setAutoOutstanding,
    autoAssessment,
    setAutoAssessment,
    jobPostingData,
    setJobPostingData,
    jobPostExist,
    jobTitleLabel,
    setJobTitleLabel,
    stages,
    setStages,
  } = props;

  const jobTitleOption = formatJobTitleOption(jobDescription?.jobTitle, jobTitleLabel);

  const getJobTitleString = (): string => {
    if (typeof jobDescription?.jobTitle === 'object' && jobDescription?.jobTitle !== null) {
      return (jobDescription.jobTitle as { jobTitle?: string }).jobTitle || '';
    }
    return jobTitleLabel || (typeof jobDescription?.jobTitle === 'string' ? jobDescription.jobTitle : '');
  };

  const getJobTitleId = (): string => {
    if (typeof jobDescription?.jobTitle === 'object' && jobDescription?.jobTitle !== null) {
      return (jobDescription.jobTitle as { id?: string }).id || '';
    }
    return typeof jobDescription?.jobTitle === 'string' ? jobDescription.jobTitle : '';
  };

  switch (currentSubTab) {
    case 'Job Requisition':
      return (
        <JobRequisitionPage
          jobRequisition={jobRequisition}
          setJobRequisition={setJobRequisition as React.Dispatch<React.SetStateAction<JobRequisition>>}
          jobDescription={jobDescription}
          setJobDescription={setJobDescription as React.Dispatch<React.SetStateAction<JobDescription>>}
          editable={editable}
          completedElement={completedElement}
          employees={defaultEmployeeUserList}
          defaultHiringManager={hiringManagerOptions}
          defaultInternalRecruiter={internalRecruiterOptions}
          setDefaultHiringManager={setHiringManagerOptions}
          setDefaultInternalRecruiter={setInternalRecruiterOptions}
        />
      );

    case 'Job Description':
      return (
        <JobDescriptionTab
          editable={editable}
          completedElement={completedElement}
          jobTitleList={jobTitleList}
          jobDescription={jobDescription}
          setJobDescription={setJobDescription as any}
          jobTitle={jobTitleOption}
          setJobTitle={() => {}}
          setJobTitleLabel={setJobTitleLabel as any}
          setAutoSuitability={setAutoSuitability as any}
          setStages={setStages as any}
        />
      );

    case 'CV Ingestion':
      return (
        <CVIngestionTab
          jobRequisition={jobRequisition}
          jobDescription={jobDescription}
          setStages={setStages}
          completedElement={completedElement}
        />
      );

    case 'Job Posting':
      return (
        <JobPosting
          jobTitleList={jobTitleList}
          setJobPostingData={setJobPostingData as any}
          setStages={setStages as any}
          jobPostingData={jobPostingData}
          jobDescription={jobDescription}
          jobPostExist={jobPostExist}
          autoAssessment={autoAssessment}
        />
      );

    case 'Auto Suitability':
      return (
        <AutoEvaluationTab
          editable={editable}
          completedElement={completedElement}
          evaluationType='Auto Suitability'
          autoEvaluation={autoSuitability}
          setAutoEvaluation={setAutoSuitability as React.Dispatch<React.SetStateAction<AutoEvaluation>>}
          jobDescription={jobDescription}
        />
      );

    case 'Auto Outstanding':
      return (
        <AutoEvaluationTab
          editable={editable}
          completedElement={completedElement}
          evaluationType='Auto Outstanding'
          autoEvaluation={autoOutstanding}
          setAutoEvaluation={setAutoOutstanding as React.Dispatch<React.SetStateAction<AutoEvaluation>>}
          importAutoSuitability={() => setAutoOutstanding(autoSuitability)}
          jobDescription={jobDescription}
        />
      );

    case 'Auto Assess Questions':
      return (
        <AutoAssessmentTab
          completedElement={completedElement}
          autoAssessment={autoAssessment}
          setAutoAssessment={setAutoAssessment as React.Dispatch<React.SetStateAction<AutoAssessment>>}
          jobDescription={jobDescription}
          jobTitle={getJobTitleString()}
          jobTitleId={getJobTitleId()}
        />
      );

    case 'Permissions':
      return (
        <Permissions
          resourceId={jobOpeningId || ''}
          resourceType='Job-Opening'
          employees={defaultEmployeeUserList}
          defaultHiringManager={
            jobRequisition?.hiringManager
              ?.map(safeJobUserToUserDisplay)
              .filter((u): u is NonNullable<typeof u> => Boolean(u)) as any
          }
          defaultInternalRecruiter={
            jobRequisition?.internalRecruiter
              ?.map(safeJobUserToUserDisplay)
              .filter((u): u is NonNullable<typeof u> => Boolean(u)) as any
          }
        />
      );

    case 'CCA Questions':
      return <CCATab setActiveTab={() => {}} />;

    default:
      return (
        <JobRequisitionPage
          jobRequisition={jobRequisition}
          setJobRequisition={setJobRequisition as React.Dispatch<React.SetStateAction<JobRequisition>>}
          jobDescription={jobDescription}
          setJobDescription={setJobDescription as React.Dispatch<React.SetStateAction<JobDescription>>}
          editable={editable}
          completedElement={completedElement}
          employees={defaultEmployeeUserList}
          defaultHiringManager={hiringManagerOptions}
          defaultInternalRecruiter={internalRecruiterOptions}
          setDefaultHiringManager={setHiringManagerOptions}
          setDefaultInternalRecruiter={setInternalRecruiterOptions}
        />
      );
  }
};
